// [VV12-TEARDOWN] lp-scrollytelling A/B test (DEV-9801).
// vv12 — the scroll-driven video narrative at /{market}/lp/rainjacket/st
// against the current V6 rain-jacket landing page at /{market}/lp/rainjacket.
// 50 / 50, all markets. Both arms sell the same `rainjacket` channel and the
// same offer; the scrollytelling arm checks out under its own path
// (/{market}/lp/rainjacket/st/checkout — a thin duplicate of the V6 checkout)
// so URL-based funnels stay separable per arm and no cross-path hop happens
// mid-funnel.
//
// Client-safe: this module must NOT import `next/server` (unlike
// `../middleware/scrollytelling.ts`), because the exposure beacon pulls it
// into client bundles on both landing pages.
//
// ─── DIRECTION: ADS LAND ON THE V6 LP, `test` IS REDIRECTED ────────────────
// The rain-jacket ad links point at `/{market}/lp/rainjacket` and CANNOT be
// repointed — hard business constraint (2026-08-07). So the V6 LP home is the
// experiment's entry: middleware buckets there and 307s the `test` arm to the
// scrollytelling page. Organic visitors to `/lp/rainjacket/st` (it is
// sitemap-listed) are bucketed too, and `control` among them is redirected to
// the V6 LP so the arms stay coherent in both directions.
//
//   ads → /{market}/lp/rainjacket           (entry — the V6 LP home)
//        · control → renders the V6 LP (stays)
//        · test    → 307 → /{market}/lp/rainjacket/st
//
//   /{market}/lp/rainjacket/st              (organic/direct only)
//        · test    → renders the scrollytelling page
//        · control → 307 → /{market}/lp/rainjacket
//
// ─── THE LOOP, AND THE DEMOTE THAT BREAKS IT ───────────────────────────────
// `useExperienceCompatibility` (@local/scrollytelling) hard-redirects a
// visitor OFF the scrollytelling page and back to the V6 LP, via
// `window.location.replace`, whenever the experience cannot render usefully:
// `prefers-reduced-motion: reduce`, `forced-colors: active`, a missing
// browser feature (ResizeObserver / PointerEvent / rAF / MP4), or a Tab
// keypress in the first 3 s. That bounce is an accessibility requirement, not
// an implementation detail, and it is not negotiable.
//
// With the entry on the V6 LP, that bounce and the test-arm redirect point at
// each other: middleware → /lp/rainjacket/st → hook bounces → /lp/rainjacket →
// middleware → /lp/rainjacket/st → … forever. The signals are client-only, so the edge
// cannot pre-check them. The escape (PR #4049 review): the bounce DEMOTES the
// visitor — `demoteVv12ToControl()` rewrites the `vv12` cookie to `control`
// and the bounce URL carries `?vv12=control` (the existing override param).
// The middleware then treats them as any other control visitor: no redirect,
// ever. No second cookie, no extra middleware state.
//
// The `?vv12=control` param is the cookie's degraded mode: with cookies
// blocked entirely, the rewrite doesn't stick and the visitor re-hashes to
// `test` on the next entry — the param stops that navigation's redirect, so
// each pageload terminates (a double hop per entry, never an infinite loop).
//
// ⚠️ KNOWN COST: the `test` arm eats the 307 on the landing hit and `control`
// does not — the redirect latency penalty biases AGAINST scrollytelling.
// Edge redirects are tens of ms; note it in the readout if the result is
// close.
//
// ─── SCOPE ─────────────────────────────────────────────────────────────────
// Assignment happens on exactly two paths: the V6 LP home (the entry) and the
// scrollytelling page. The cookie is written `path=/`, so it rides along to
// the sub-pages and the checkouts without those being in scope. Every visitor
// landing on the V6 LP home is bucketed — organic included — which matches
// the brief's baseline (its 144k/mo "unique LP visitors" ARE this page's
// visitors), so the brief's sample-size table applies as written.
//
// The rain-jacket AINCHOR sub-pages (`/{market}/lp/rainjacket/{ainchor}`) are
// a third surface, but a DEMOTE surface, not an assignment one: they are ad
// landing pages that render the control-style V6 experience, so any visit
// there pins the visitor to `control` — cookieless visitors are pinned before
// they can ever be bucketed `test` on a later entry-page hit, and an existing
// `test` cookie is demoted (that visitor just consumed control content, their
// test arm is contaminated either way; their earlier `test` exposure makes
// PostHog bucket them `$multiple` and drop them, same as the bounce demote).
// No redirect and no exposure ever fires there — `isVv12ExposurePath` stays
// entry + scrollytelling only.
//
// ─── EXPOSURE — A/B/C, WHERE C IS THE DISCARD GROUP ────────────────────────
// The brief's PRIMARY metric is %LP → Checkout, which dictates where exposure
// fires: on the LANDING PAGES, never on the checkout. vv10 fired exposure on
// the checkout and its checkout-rate metric came out at ~98% — arithmetically
// forced, because enrolment happened at the checkout itself. Do NOT add vv12
// to `checkoutExposure.ts`; that shim exists for experiments whose primary
// metric is the checkout `$pageview`, which this one's is not.
//
// C = `bailout`, the third variant key on the PostHog flag. The beacon runs
// `getVv12EnvIncompatibility()` — the deterministic subset of the bounce's
// checks — on BOTH landing pages, and a visitor who fails it is exposed as
// `bailout` (with `vv12_bailout_reason`) instead of an arm. Arm-enrolling
// them would pollute `test` with mechanical exits and control-page
// conversions; silently dropping them would hide how many people bail and
// why. The divert is symmetric (both arms), so the A/B comparison and its SRM
// stay valid. Compare A vs B only; C is observation, not a treatment. The
// A/B read covers the scrollytelling-CAPABLE population — say so in the
// readout.
//
// Known losses, accepted for simplicity (PR #4049 review):
//   - A Tab-within-3s bounce is temporal, so the probe can't see it and the
//     demote leaves no trace: those visitors surface as `control` from their
//     next pageload on (their pre-bounce `test` exposure, if it won the race
//     with the bounce, makes PostHog bucket them `$multiple` and drop them).
//     C therefore counts env-driven bailouts only.
//   - Post-demote, the original assignment is unrecoverable, so C exposures
//     carry the reason but not the assigned arm.
//
// ─── TEARDOWN CHECKLIST (grep `VV12-TEARDOWN` across the repo) ─────────────
// DELETE these files (entirely test-only):
//   packages/tracking/src/experiments/scrollytelling.ts        (this file)
//   packages/tracking/src/middleware/scrollytelling.ts
//   packages/tracking/src/__tests__/scrollytellingMiddleware.test.ts
//   packages/tracking/src/__tests__/scrollytellingExperiment.test.ts
//   packages/ui/src/components/Blocks/Highlight/Vv12ExposureBeacon.tsx
//
// REVERT the marked `[VV12-TEARDOWN]` blocks in:
//   packages/tracking/src/middleware/index.ts      (option, resolver, redirect, header, cookie)
//   packages/tracking/src/middleware/url/const.ts  (drop the "vv12" entry outright — #4026 convention)
//   packages/tracking/src/trackActions.tsx         (split_test membership)
//   apps/store/src/proxy.ts                        (drop `vv12: true`)
//   packages/offer/src/builder.tsx                 (control-arm beacon mount)
//   packages/scrollytelling/src/components/ScrollytellingStageClient.tsx (test-arm beacon mount)
//   packages/scrollytelling/src/hooks/useExperienceCompatibility.ts (demote + param on the bounce)
//   packages/devtools/src/panel/components/ABTestingTab.tsx (vv12 EXPERIMENTS entry)
//   packages/payment/src/modules/stripe/useStripeCheckout.ts  (order tag in prepareStripeMetadata)
//   packages/payment/src/modules/paypal/shared.ts             (order tag in preparePayPalMetadata)
//   packages/payment/src/helpers/getFreeOrderSessionBody.ts   (order tag in the metadata body)
//
// ⚠️ Those three payment files share one `import Cookies from "js-cookie"` with
// the vv5 and vv11 teardown blocks around each VV12 block. Whichever experiment
// is torn down LAST owns that import — do not delete it with an earlier one.
//
// NOT experiment code (do not tear down): the scrollytelling checkout route
// (apps/store/.../lp/[channel]/st/checkout) and SCROLLYTELLING_CHECKOUT_PATH —
// the scrollytelling LP owns its checkout path regardless of the test.
//
// EXTERNAL: archive the `lp-scrollytelling` PostHog feature flag. If `test`
// wins, the scrollytelling page becomes the rain-jacket LP outright (the
// brief's stated intent: "becomes the template for future LPs") — graduate it
// by moving the route, NOT by leaving this cookie gate in place. The
// accessibility bounce in `useExperienceCompatibility` predates this test and
// stays either way.
// ───────────────────────────────────────────────────────────────────────────

/** Cookie / query-override slot name for the vv12 experiment. */
export const VV12_COOKIE = "vv12"

/**
 * PostHog feature-flag key (single source of truth). The flag carries THREE
 * variant keys — `control` / `test` / `bailout` — matching what the beacon
 * reports. Human-readable and distinct from the cookie shorthand, per house
 * convention (vv10 → `lp-sku-pricing-modifiers`, vv11 → `lp-no-discount-framing`).
 */
export const VV12_FEATURE_FLAG = "lp-scrollytelling"

/**
 * The channel both arms sell — the `{channel}` in both arms' URLs. Control's
 * LP lives at `/{market}/lp/rainjacket` (also the compatibility-bounce
 * target); the scrollytelling arm hangs off it at `/{market}/lp/rainjacket/st`.
 */
export const SCROLLYTELLING_LP_CHANNEL = "rainjacket"

/**
 * Valid ASSIGNMENT values — what the middleware buckets and the cookie holds.
 *   - control → today's V6 rain-jacket LP (the entry page; stays put)
 *   - test    → 307 from the entry to the scrollytelling page
 *
 * `bailout` is deliberately NOT here: it is not an assignment, it is a
 * reporting bucket the beacon substitutes at exposure time (see
 * `VV12_BAILOUT_GROUP`). The middleware can't detect compatibility — the
 * signals are client-only — so the cookie stays two-valued.
 */
export const VV12_VARIANTS = ["control", "test"] as const

export type Vv12Variant = (typeof VV12_VARIANTS)[number]

/** Type guard for anything read back from the cookie / query override. */
export const isVv12Variant = (value: unknown): value is Vv12Variant =>
  VV12_VARIANTS.includes(value as Vv12Variant)

/**
 * The C group — the third variant key on the PostHog flag. Exposed instead of
 * the assigned arm for visitors the compatibility probe diverts, on BOTH
 * landing pages, so the discard is symmetric and observable (volume + reason)
 * rather than silent. Compare A vs B only; C is observation.
 */
export const VV12_BAILOUT_GROUP = "bailout" as const

/** What `$feature_flag_response` can carry: an arm, or the C group. */
export type Vv12ExposureGroup = Vv12Variant | typeof VV12_BAILOUT_GROUP

// ─── Path scope ────────────────────────────────────────────────────────────

/**
 * Whether a pathname is the scrollytelling landing page for this experiment —
 * exactly `/{market}/lp/rainjacket/st`, nothing beneath it and no look-alike
 * channel. The channel segment is matched whole and the path must terminate at
 * `st` or a single trailing slash, so neither `…/lp/rainjacket-v2/st` nor
 * `…/lp/rainjacket/st/checkout` matches — the bug class that polluted vv5 (a
 * persisted cookie changing behaviour on `sunbeater-hoodie-sale` — DEV-9882 /
 * PR #3975). The checkout sub-path is out of scope like the V6 checkout.
 */
const SCROLLYTELLING_LP_PATH_RE = new RegExp(
  `^/[^/]+/lp/${SCROLLYTELLING_LP_CHANNEL}/st/?$`
)

export const isScrollytellingLpPath = (pathname?: string | null): boolean =>
  !!pathname && SCROLLYTELLING_LP_PATH_RE.test(pathname)

/**
 * Whether a pathname is the V6 LP home — exactly `/{market}/lp/rainjacket`,
 * the experiment's entry page (the ad URL, which may carry a trailing slash).
 * Deliberately NOT its ainchor sub-pages (their own demote surface — see
 * `isAinchorLpPath`) or its checkout: bucketing those would enrol visitors
 * who never hit the page under test.
 */
const STANDARD_LP_PATH_RE = new RegExp(
  `^/[^/]+/lp/${SCROLLYTELLING_LP_CHANNEL}/?$`
)

export const isStandardLpPath = (pathname?: string | null): boolean =>
  !!pathname && STANDARD_LP_PATH_RE.test(pathname)

/**
 * Whether a pathname is a rain-jacket ainchor sub-page — exactly
 * `/{market}/lp/rainjacket/{ainchor}`, one segment under the channel that is
 * neither the scrollytelling page (`st`) nor the checkout (the two reserved
 * static segments at that depth), optional trailing slash (ad links carry
 * one). These render the control-style V6 experience, so the middleware
 * DEMOTES here — it never assigns, redirects, or fires exposure (see the
 * SCOPE note in the header).
 */
const AINCHOR_LP_PATH_RE = new RegExp(
  `^/[^/]+/lp/${SCROLLYTELLING_LP_CHANNEL}/(?!(?:st|checkout)/?$)[^/]+/?$`
)

export const isAinchorLpPath = (pathname?: string | null): boolean =>
  !!pathname && AINCHOR_LP_PATH_RE.test(pathname)

/**
 * Whether this pathname is a surface where vv12 exposure should fire — either
 * arm's landing page. The checkouts are deliberately absent (see the EXPOSURE
 * note at the top of this file).
 */
export const isVv12ExposurePath = (pathname?: string | null): boolean =>
  isScrollytellingLpPath(pathname) || isStandardLpPath(pathname)

// ─── Compatibility: the demote + the C-group probe ─────────────────────────

/**
 * Why the scrollytelling experience was refused — what
 * `getVv12EnvIncompatibility` can detect and `vv12_bailout_reason` can carry.
 * The bounce's fourth trigger (a Tab keypress in the first 3 s) is temporal,
 * leaves no environment trace, and is deliberately absent (see the header).
 */
export const VV12_BAILOUT_REASONS = [
  "reduced-motion",
  "forced-colors",
  "unsupported",
] as const

export type Vv12BailoutReason = (typeof VV12_BAILOUT_REASONS)[number]

const DEMOTE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30 // 30 days — matches middleware

/**
 * The bounce's loop-breaker: rewrite the `vv12` cookie to `control` so the
 * middleware never redirects this visitor toward the scrollytelling page
 * again. A plain `document.cookie` write with the same attributes middleware
 * uses (host-only, `path=/`, `sameSite=lax`, 30 d), so it is the SAME cookie,
 * not a shadowing duplicate.
 *
 * Wrapped: cookie access throws outright in some privacy modes, and this must
 * never be the thing that breaks an accessibility redirect (the
 * `?vv12=control` param on the bounce URL covers that navigation regardless —
 * see `withVv12ControlParam`).
 */
export const demoteVv12ToControl = (): void => {
  if (typeof document === "undefined") return
  try {
    document.cookie = `${VV12_COOKIE}=control; max-age=${DEMOTE_COOKIE_MAX_AGE_SECONDS}; path=/; samesite=lax`
  } catch {
    // cookie unavailable — the ?vv12=control param still breaks the loop
  }
}

/**
 * Append `?vv12=control` to the bounce target — the demote's degraded mode
 * for cookie-blocked browsers. It is the existing middleware override param,
 * so no extra middleware state exists for it. `path` is a same-origin
 * pathname from `STANDARD_LP_PATH`, never user input.
 */
export const withVv12ControlParam = (path: string): string =>
  `${path}${path.includes("?") ? "&" : "?"}${VV12_COOKIE}=control`

/**
 * The deterministic subset of `useExperienceCompatibility`'s checks — the
 * C-group probe. Runs on BOTH landing pages so the divert is symmetric:
 * a visitor this returns non-null for is exposed as `bailout` on either arm,
 * never as an arm.
 *
 * ⚠️ MUST MIRROR `useExperienceCompatibility` (@local/scrollytelling) — if a
 * check is added or removed there, change it here too, or the probe and the
 * bounce disagree and bounced-but-arm-enrolled users reappear.
 *
 * Fails closed: any error means "not compatible", because a false divert to C
 * is harmless while arm-enrolling someone the hook will bounce is the exact
 * bias the C group exists to prevent.
 */
export const getVv12EnvIncompatibility = (): Vv12BailoutReason | null => {
  if (typeof window === "undefined") return "unsupported"
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return "reduced-motion"
    }
    if (window.matchMedia("(forced-colors: active)").matches) {
      return "forced-colors"
    }
    if (!window.ResizeObserver) return "unsupported"
    if (typeof window.PointerEvent === "undefined") return "unsupported"
    if (typeof window.requestAnimationFrame !== "function") return "unsupported"
    const probe = document.createElement("video")
    if (!probe.canPlayType("video/mp4")) return "unsupported"
    return null
  } catch {
    return "unsupported"
  }
}

// ─── Order metadata ────────────────────────────────────────────────────────

/**
 * Order-payload tag for server-side per-arm revenue analysis. The brief lists
 * Revenue Per Session and AOV as secondary metrics, and client-side PostHog
 * silently loses ad-blocker users, so revenue is also stamped on the order
 * itself (same approach as vv5's `freeShippingOrderTag`).
 *
 * Returns `{}` unless the order carries a valid variant, so unrelated orders
 * are never tagged. Pure — callers pass the cookie value — so it stays
 * edge-safe and unit-testable.
 */
export const scrollytellingOrderTag = (
  vv12: string | undefined
): { experiment_lp_scrollytelling: Vv12Variant } | Record<string, never> =>
  isVv12Variant(vv12) ? { experiment_lp_scrollytelling: vv12 } : {}
